import React, { Fragment, useState } from 'react';

import './your-current-rewards-banner.css';
import Image from 'next/image';

import { useTimeLeft } from '../../../../../packages/ui/lib/hooks';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { RewardItem } from './reward-item';
import { LeaderboardReward } from '@wwe-portal/entities/rbe.type';

export const YourCurrentRewardsBanner = ({
  endDate,
  currentPlayerRewards,
}: {
  endDate: string;
  currentPlayerRewards: Partial<LeaderboardReward>[];
}) => {
  const { ended } = useTimeLeft(endDate);
  const { isMobile } = useResponsive();
  const [isShowAllRewards, setIsShowAllRewards] = useState(false);

  const rewards = currentPlayerRewards;
  const isShowRewardsMoreButton =
    currentPlayerRewards && currentPlayerRewards.length > 3 && isMobile;
  const rewardsCount = currentPlayerRewards?.length;

  const maxShowingRewards = isMobile ? 3 : 8;
  if (!ended) {
    return null;
  }

  return (
    <>
      <div className="rewards-claim-section-title flex items-center gap-1">
        <span>Leaderboard Rewards</span>
        <span>
          <Image
            width={16}
            height={16}
            alt="circle-check"
            src="/assets/icons/circle-check.svg"
          />
        </span>
      </div>
      <div
        className="rewards-claim-section-wrapper"
        style={{ height: isShowAllRewards ? 160 : '100%' }}
      >
        <Image
          width={180}
          height={150}
          className="decor"
          src="/assets/icons/reward-claim-section-decor.svg"
          alt="decor"
        />
        <Image
          width={180}
          height={150}
          className="decor right"
          src="/assets/icons/reward-claim-section-decor.svg"
          alt="decor"
        />
        <div className="current-player-claimable-rewards-wrapper items-center gap-3">
          {currentPlayerRewards
            ?.slice(0, maxShowingRewards)
            .map((reward, index, rewardsArr) => {
              const isShowPlusIcon =
                index < rewardsArr.length - 1 || isShowRewardsMoreButton;
              return (
                <Fragment key={index}>
                  <div className="bg-bgColor8 rounded-6 relative flex h-10 w-10 items-center justify-center md:h-[60px] md:w-[60px]">
                    <div className="h-10 w-10">
                      <RewardItem
                        rewardItem={reward}
                        size="default"
                        position="center"
                        containerClassName="w-full h-full bg-transparent"
                      />
                      <p className="text-inlineTextColor10 absolute bottom-0 w-full translate-y-1/2 stroke-2 text-center text-xs font-semibold">
                        x{reward.rewardQuantity}
                      </p>
                    </div>
                  </div>
                  {isShowPlusIcon ? (
                    <Image
                      alt="plus-icon"
                      src="/assets/icons/brackets-plus-icon.svg"
                      className="h-3 w-3"
                      width={12}
                      height={12}
                    />
                  ) : null}
                </Fragment>
              );
            })}
          {isShowRewardsMoreButton && rewardsCount ? (
            <div
              data-testid="rewards-claim-section-more-button"
              onClick={() => setIsShowAllRewards(true)}
              className="more-button"
            >
              <p>+{rewardsCount - 3} More </p>
            </div>
          ) : null}
        </div>
        {isShowAllRewards && rewards ? (
          <AllRewardsMenu
            rewards={rewards}
            onClose={() => setIsShowAllRewards(false)}
          />
        ) : null}
      </div>
    </>
  );
};

const AllRewardsMenu = ({
  rewards,
  onClose,
}: {
  rewards: Partial<LeaderboardReward>[];
  onClose: () => void;
}) => {
  const { isMobile } = useResponsive();
  const rewardsFirstContainerLength = Math.ceil(rewards.length / 2);

  const renderRewards = (rewards: Partial<LeaderboardReward>[]) => {
    return rewards.map((reward, index) => {
      const rewardItem = reward.rewardCurrency || reward.rewardInGameItem;

      const rewardName =
        typeof rewardItem === 'object'
          ? rewardItem.offerDisplayName
          : rewardItem;

      return (
        <div
          data-testid="all-rewards-menu-reward"
          key={index}
          className="reward-info-row"
        >
          <RewardItem rewardItem={reward} />
          <p className="info-text">
            {reward.rewardQuantity} x {rewardName}
          </p>
        </div>
      );
    });
  };

  return (
    <div className={'all-rewards-menu-wrapper'}>
      <Image
        width={16}
        height={16}
        alt="close-icon"
        src="/assets/icons/close-icon.svg"
        className="close-icon"
        onClick={onClose}
      />
      <div className="all-rewards-wrapper">
        {isMobile ? (
          renderRewards(rewards)
        ) : (
          <div className="rewards-containers">
            <div className="rewards-first-container">
              {renderRewards(rewards.slice(0, rewardsFirstContainerLength))}
            </div>
            <div className="rewards-second-container">
              {renderRewards(rewards.slice(rewardsFirstContainerLength))}
            </div>
          </div>
        )}
      </div>

      {!isMobile ? (
        <div
          data-testid="all-rewards-menu-separating-line"
          className="separating-line"
        />
      ) : null}
    </div>
  );
};
